import type { CamposCapturaDTO } from '@/features/captura/tipos';

/**
 * Como os campos lidos pelo celular (ou pela câmera do próprio aparelho)
 * entram no formulário da nova O.S. Função pura: a página aplica o
 * resultado com os setters que já existem (`aoMudarPlaca`, `setDadosVeiculo`,
 * `setKmRegistrado`), sem regra nova escondida no componente.
 */

/** Mesmos campos do formulário de veículo novo da O.S. (texto, como nos inputs). */
export type DadosVeiculoCaptura = {
  marca: string;
  modelo: string;
  ano: string;
  motor: string;
  km: string;
};

export type VeiculoParaCaptura = { id: string; placa: string | null };

export type EstadoFormularioCaptura = {
  dadosVeiculo: DadosVeiculoCaptura;
  kmRegistrado: string;
  semPlaca: boolean;
  /** Id do veículo existente já escolhido na placa, se houver. */
  veiculoSelecionadoId: string | null;
  /** Placa nova já digitada (tipo `novo`), se houver. */
  placaNovaAtual: string | null;
  /** Veículos do cliente escolhido (`veiculosCliente`). */
  veiculos: VeiculoParaCaptura[];
};

export type AcaoPlacaCaptura =
  | { tipo: 'manter' }
  | { tipo: 'existente'; veiculoId: string; placa: string }
  | { tipo: 'novo'; placa: string };

export type ResultadoCamposCapturados = {
  /** O que fazer com a seleção da placa (antes de aplicar o resto). */
  placa: AcaoPlacaCaptura;
  /** Dados do veículo novo / sem placa já mesclados. */
  dadosVeiculo: DadosVeiculoCaptura;
  /** KM do card "KM no atendimento" (veículo existente). */
  kmRegistrado: string;
  /** A placa chegou e o formulário estava em "Sem placa". */
  desmarcarSemPlaca: boolean;
  /** Placa normalizada recebida (7 caracteres) ou null. */
  placaRecebida: string | null;
};

const KM_MAXIMO = 9_999_999;

/** Placa só com A-Z/0-9, maiúscula, 7 caracteres — o formato que a O.S. grava. */
export function normalizarPlacaCaptura(placa: string | null | undefined): string {
  return (placa ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 7);
}

/** "ABC1D23" → "ABC-1D23" (só para exibir). */
export function formatarPlacaExibicao(placa: string): string {
  const p = normalizarPlacaCaptura(placa);
  return p.length === 7 ? `${p.slice(0, 3)}-${p.slice(3)}` : p;
}

function kmValido(km: unknown): string | null {
  if (typeof km !== 'number' || !Number.isFinite(km)) return null;
  const inteiro = Math.trunc(km);
  if (inteiro < 0 || inteiro > KM_MAXIMO) return null;
  return String(inteiro);
}

function texto(v: string | undefined, max: number): string {
  return (v ?? '').trim().slice(0, max);
}

/** Mescla só o que veio preenchido — nunca apaga o que o usuário digitou. */
function mesclarDados(
  atual: DadosVeiculoCaptura,
  campos: CamposCapturaDTO,
  incluirKm: boolean,
): DadosVeiculoCaptura {
  const marca = texto(campos.marca, 60);
  const modelo = texto(campos.modelo, 80);
  const ano = (campos.ano ?? '').replace(/\D/g, '').slice(0, 4);
  const motor = texto(campos.motor, 60);
  const km = incluirKm ? kmValido(campos.km) : null;
  return {
    marca: marca || atual.marca,
    modelo: modelo || atual.modelo,
    ano: ano.length === 4 ? ano : atual.ano,
    motor: motor || atual.motor,
    km: km ?? atual.km,
  };
}

export function aplicarCamposCapturados(
  estado: EstadoFormularioCaptura,
  campos: CamposCapturaDTO,
): ResultadoCamposCapturados {
  const placa = normalizarPlacaCaptura(campos.placa);
  const km = kmValido(campos.km);

  if (placa.length === 7) {
    const existente = estado.veiculos.find((v) => normalizarPlacaCaptura(v.placa) === placa);
    if (existente) {
      // Veículo já cadastrado do cliente: seleciona e aplica só o KM. Marca,
      // modelo etc. já estão no cadastro e o formulário de veículo novo some.
      const mesmoVeiculo = existente.id === estado.veiculoSelecionadoId;
      return {
        placa: mesmoVeiculo
          ? { tipo: 'manter' }
          : { tipo: 'existente', veiculoId: existente.id, placa },
        dadosVeiculo: estado.dadosVeiculo,
        // Trocar de veículo zera o KM digitado para o anterior (igual ao `aoMudarPlaca`).
        kmRegistrado: km ?? (mesmoVeiculo ? estado.kmRegistrado : ''),
        desmarcarSemPlaca: estado.semPlaca,
        placaRecebida: placa,
      };
    }
    // Placa nova (ou de outro cliente — o fluxo de transferência no salvar cuida).
    // O KM vai em `dadosVeiculo.km`, que é o campo do formulário de veículo novo.
    const mesmaPlaca =
      !!estado.placaNovaAtual && normalizarPlacaCaptura(estado.placaNovaAtual) === placa;
    return {
      placa: mesmaPlaca ? { tipo: 'manter' } : { tipo: 'novo', placa },
      dadosVeiculo: mesclarDados(estado.dadosVeiculo, campos, true),
      kmRegistrado: mesmaPlaca ? estado.kmRegistrado : '',
      desmarcarSemPlaca: estado.semPlaca,
      placaRecebida: placa,
    };
  }

  // Sem placa válida: completa o que já está na tela.
  if (estado.veiculoSelecionadoId) {
    return {
      placa: { tipo: 'manter' },
      dadosVeiculo: estado.dadosVeiculo,
      kmRegistrado: km ?? estado.kmRegistrado,
      desmarcarSemPlaca: false,
      placaRecebida: null,
    };
  }
  return {
    placa: { tipo: 'manter' },
    dadosVeiculo: mesclarDados(estado.dadosVeiculo, campos, true),
    kmRegistrado: estado.kmRegistrado,
    desmarcarSemPlaca: false,
    placaRecebida: null,
  };
}
